import React from "react";

import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import VisibilityIcon from "@mui/icons-material/Visibility";

import { ListComponent } from "@auto/widgets/ListComponent";
import { rest } from "@auto/services";
import { getServiceUri } from "@auto/metadata";

const DEFAULT_PAGE_SIZE = 50;

const formatDateTime = (value) => {
  if (!value) return "";
  if (typeof value === "string") return value.replace("T", " ").slice(0, 19);
  try {
    return new Date(value).toISOString().replace("T", " ").slice(0, 19);
  } catch {
    return String(value);
  }
};

const formatAdditionalValue = (value, displayType) => {
  if (value === null || value === undefined || value === "") return "";
  if (displayType === "datetime") return formatDateTime(value);
  if (displayType === "date" || displayType === "time") return String(value);
  if (displayType === "boolean") return value ? "Yes" : "No";
  if (displayType === "json") {
    try { return typeof value === "string" ? value : JSON.stringify(value); }
    catch { return String(value); }
  }
  return String(value);
};

const getField = (row, camelName, snakeName) =>
  row?.[camelName] ?? row?.[snakeName] ?? "";

const fullName = (row) => [
  getField(row, "firstName", "first_name"),
  getField(row, "lastName", "last_name")
].map((value) => String(value || "").trim()).filter(Boolean).join(" ");

const escapeRsqlRegexValue = (value) => String(value || "")
  .trim()
  .replace(/\\/g, "\\\\")
  .replace(/"/g, '\\"')
  .replace(/[.*+?^{}$()|[\]\\]/g, "\\$&");

const buildSearchQuery = (search) => {
  const raw = String(search || "").trim();
  const regex = escapeRsqlRegexValue(raw);
  if (!regex) return "";

  const parts = [
    `username=re="(?i).*${regex}.*"`,
    `firstName=re="(?i).*${regex}.*"`,
    `lastName=re="(?i).*${regex}.*"`,
    `nationalId=re="(?i).*${regex}.*"`,
    `deviceId=re="(?i).*${regex}.*"`,
    `requestStatus=re="(?i).*${regex}.*"`
  ];

  if (/^\d+$/.test(raw)) parts.push(`gid==${Number(raw)}`);
  return parts.join(",");
};

const buildSort = (sort) => (Array.isArray(sort) ? sort : [])
  .map((item) => ({
    selector: item?.column?.sortSelector || item?.field,
    direction: item?.direction
  }))
  .filter((item) => item.selector && (item.direction === "asc" || item.direction === "desc"));

const normalizeRow = (row) => ({
  ...row,
  gid: getField(row, "gid", "gid"),
  fullName: fullName(row),
  username: getField(row, "username", "username"),
  nationalId: getField(row, "nationalId", "national_id"),
  requestStatus: getField(row, "requestStatus", "request_status"),
  deviceId: getField(row, "deviceId", "device_id"),
  submittedAtFormatted: formatDateTime(getField(row, "submittedAt", "submitted_at")),
  votingCenterGid: formatAdditionalValue(
    getField(row, "votingCenterGid", "voting_center_gid"),
    "text"
  )
});

export const OperatorAccountRequestListWidget = ({
  gui,
  enrichRows,
  extraColumns = [],
  renderActions
}) => {
  const projection = React.useMemo(() => ({
    storageKey: "security.operator-account-request",
    columns: [
      { title: "Name", field: "fullName", sortSelector: "lastName" },
      { title: "Username", field: "username", sortSelector: "username" },
      { title: "National ID", field: "nationalId", sortSelector: "nationalId" },
      { title: "Status", field: "requestStatus", sortSelector: "requestStatus" },
      { title: "Submitted at", field: "submittedAtFormatted", sortSelector: "submittedAt" },
      { title: "Device ID", field: "deviceId", sortSelector: "deviceId" },
      { title: "Voting Center Gid", field: "votingCenterGid", sortSelector: "votingCenterGid" },
      ...extraColumns,
      { title: "Actions", field: "__actions", sorting: false, filtering: false }
    ],
    actions: (row, currentGui) => (
      <>
        <Tooltip title="View">
          <IconButton
            size="small"
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              currentGui.goTo(`/operator-account-request/${row.gid}`);
            }}
          >
            <VisibilityIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        {renderActions ? renderActions(row, currentGui) : null}
      </>
    ),
    fetch: async (query) => {
      const page = Number.isFinite(query?.page) ? query.page : 0;
      const pageSize = Number.isFinite(query?.pageSize) ? query.pageSize : DEFAULT_PAGE_SIZE;
      const payload = {
        columns: [],
        query: buildSearchQuery(query?.search) || null,
        limit: pageSize,
        offset: page * pageSize,
        sort: buildSort(query?.sort)
      };

      const [rows, totalCount] = await Promise.all([
        rest.request(getServiceUri() + "operator-account-request/search", "POST", payload),
        rest.request(getServiceUri() + "operator-account-request/count", "POST", {
          columns: [], query: payload.query, limit: null, offset: null
        })
      ]);

      let data = (rows || []).map(normalizeRow);
      if (enrichRows) data = await enrichRows(data, gui);
      return { data, totalCount, page };
    }
  }), [enrichRows, extraColumns, gui, renderActions]);

  return <ListComponent gui={gui} projection={projection} />;
};

export default OperatorAccountRequestListWidget;
